export type ConditionType = 'datetime' | 'string' | 'numeric' | 'bool' | 'array';

export type ConditionOperator =
    | 'equal_to' | 'not_equal_to' | 'greater_than' | 'less_than'
    | 'greater_than_equal_to' | 'less_than_equal_to' | 'contains'
    | 'in' | 'not_in' | 'is_null' | 'is_not_null' | 'contains_any'
    | 'not_contains_any';

export type RuleCondition = {
    type: ConditionType;
    name: string;
    operator: ConditionOperator;
    value: any;
    composite_type?: 'any' | 'all' | null;
    parent_condition_id?: string | null;
};

export type ActionType = 'then' | 'else';
export type ActionDataType = 'ADD_TAG' | 'SKIP' | 'REMOVE_TAG' | 'NOTIFY';

export type RuleAction = {
    action_type: ActionType;
    action_data: { type: ActionDataType; [key: string]: unknown };
};

export type ConditionGroupPayload = {
    behavior: 'all' | 'any';
    sort_order: number;
    conditions: Array<{
        type: ConditionType;
        name: string;
        operator: ConditionOperator;
        value: any;
    }>;
    childGroups: ConditionGroupPayload[];
};

export type RuleDto = {
    id: string;
    cid: string;
    tid?: string;
    rule_set_id?: string | null;
    priority: number;
    conditionGroups?: ConditionGroupPayload[];
    conditions?: RuleCondition[];
    actions?: RuleAction[];
    createdAt?: string | null;
    updatedAt?: string | null;
};

export type RuleOption = { id: string; displayValue: string; value: string; cid: string };

// ─── Rule Builder UI types ────────────────────────────────────────────────────

export type BuilderMode = 'simple' | 'advanced';
export type LogicOp = 'and' | 'or';

export type CondEntry = {
    kind: 'cond';
    id: string;
    fieldPath: string;
    fieldType: ConditionType;
    operator: ConditionOperator;
    value: any;
};

export type GroupNode = {
    kind: 'group';
    id: string;
    logic: LogicOp;
    children: Array<CondEntry | GroupNode>;
};

export type ActionEntry = {
    id: string;
    actionType: ActionDataType;
};


export const CONDITION_TYPES: { label: string; value: ConditionType }[] = [
    { label: 'Datetime', value: 'datetime' },
    { label: 'String',   value: 'string'   },
    { label: 'Number',   value: 'numeric'  },
    { label: 'Boolean',  value: 'bool'     },
    { label: 'Array',    value: 'array'    },
];

export const OPERATORS_BY_TYPE: Record<ConditionType, { label: string; value: ConditionOperator }[]> = {
    datetime: [
        { label: 'Less than',    value: 'less_than'    },
        { label: 'Greater than', value: 'greater_than' },
        { label: 'Equals',       value: 'equal_to'     },
        { label: 'Not equals',   value: 'not_equal_to' },
    ],
    string: [
        { label: 'Equals',           value: 'equal_to'         },
        { label: 'Not equals',       value: 'not_equal_to'     },
        { label: 'Contains',         value: 'contains'         },
        { label: 'Contains any',     value: 'contains_any'     },
        { label: 'Not contains any', value: 'not_contains_any' },
        { label: 'Not in',           value: 'not_in'           },
        { label: 'Is empty',         value: 'is_null'          },
        { label: 'Is not empty',     value: 'is_not_null'      },
    ],
    numeric: [
        { label: 'Less than',       value: 'less_than'             },
        { label: 'Greater than',    value: 'greater_than'          },
        { label: 'Less or Equal',   value: 'less_than_equal_to'    },
        { label: 'Greater or Equal',value: 'greater_than_equal_to' },
        { label: 'Equals',          value: 'equal_to'              },
        { label: 'Not equals',      value: 'not_equal_to'          },
    ],
    bool: [
        { label: 'Equals',     value: 'equal_to'     },
        { label: 'Not equals',   value: 'not_equal_to' },
    ],
    array: [
        { label: 'Contains any',     value: 'contains_any'     },
        { label: 'Not contains any', value: 'not_contains_any' },
        { label: 'Not in',           value: 'not_in'           },
    ],
};

export const DATETIME_SPECIAL_VALUES = [
    { label: 'NOW (current time)',   value: 'NOW'   },
    { label: 'TODAY (start of day)', value: 'TODAY' },
];

export const DATETIME_CUSTOM_SENTINEL = '__custom_date__';

export const ACTION_ICONS: Record<ActionDataType, string> = {
    ADD_TAG:    '🏷',
    REMOVE_TAG: '✂',
    SKIP:       '⊘',
    NOTIFY:     '🔔',
};

export const ACTION_DATA_TYPES: { label: string; value: ActionDataType }[] = [
    { label: 'Add Tag',    value: 'ADD_TAG'    },
    { label: 'Skip',       value: 'SKIP'       },
    { label: 'Remove Tag', value: 'REMOVE_TAG' },
    { label: 'Notify',     value: 'NOTIFY'     },
];

export const COMPOSITE_OPTIONS = [
    { label: 'None',      value: 'none' },
    { label: 'ALL (AND)', value: 'all'  },
    { label: 'ANY (OR)',  value: 'any'  },
];